import {
  IonCard,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  type RefresherEventDetail,
  useIonRouter,
} from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import { format } from "@formkit/tempo";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { BackButton } from "@/components/back-button";
import { Button } from "@/components/button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { useQRScanner } from "@/components/global/qr-scanner/hooks/use-qr-scanner";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { BreakTimeButton } from "@/components/shift/break-time-button";
import { BreakTimeModal } from "@/components/shift/break-time-modal";
import { QRClockButtons } from "@/components/shift/qr-clock-buttons";
import { ShiftStatusCard } from "@/components/shift/status-card";
import { Toolbar } from "@/components/toolbar";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { crossIcon } from "@/lib/icons/cross.icon";
import { timeIcon } from "@/lib/icons/time.icon";
import { queryClient } from "@/lib/providers/query";
import { queryOptions_shift_single } from "@/lib/providers/query/functions/shift/single";
import { Routes } from "@/lib/router";
import { cx } from "@/lib/style/cva.config";
import { logger } from "@/logger";
import {
  calculateShiftDuration,
  durationToMinutes,
  formatDurationToISO,
  formatDurationToTimeString,
  minutesToDuration,
  parseTimeString,
} from "@/utils/time";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";
import { TravelExpensesSection } from "./travel-expenses.section";

const TOAST_KEY = "shift-page";

export function ShiftPage() {
  const { t, i18n } = useTranslation();
  const router = useIonRouter();
  const { id } = Routes.Shift.useParams();
  const { modal } = Routes.Shift.useSearchParams();
  const validator = useValidator();
  const statusBarHeight = useStatusBarHeight();
  const [showBreakTimeModal, setShowBreakTimeModal] = useState(false);

  const { data: shift, refetch: refetchShift } = useQuery({
    ...queryOptions_shift_single(id!),
    enabled: !!id,
  });

  const hasShiftTimeEntry = !!shift?.shiftTimeEntry?.id;
  const isProjectFinalized = shift?.project?.status === "FINALIZED";
  const allowEmployeeSubmitHours =
    !isProjectFinalized && !!shift?.project?.allowEmployeeSubmitHours;
  const allowTravelExpenses =
    !isProjectFinalized && !!shift?.project?.allowEmployeeSubmitDeclarations;
  const enableQRCodeClocking = !!shift?.project?.enableQRCodeClocking;

  // Clock status
  // IMPORTANT: Shift time entries work as follows:
  // 1. No time entry (hasShiftTimeEntry = false) → Can clock in OR declare hours manually
  // 2. Has startAt but no endAt → Currently clocked in, can only clock out
  // 3. Has both startAt and endAt → Shift complete, no further actions allowed
  // 4. If ANY time entry exists (QR or manual), the other method should be disabled

  const hasStartTime = !!shift?.shiftTimeEntry?.startAt;
  const hasEndTime = !!shift?.shiftTimeEntry?.endAt;
  const isClockedIn = hasStartTime && !hasEndTime;
  const isClockedOut = hasStartTime && hasEndTime;

  const { data: expenseClaims, refetch: refetchExpenseClaims } = useQuery({
    queryKey: ["shift", id, "expense-claims", shift?.shiftTimeEntry?.id],
    queryFn: () => {
      const shiftTimeEntryId = shift?.shiftTimeEntry?.id;
      if (!shiftTimeEntryId) {
        return null;
      }
      return extractionWrapper(api.expenseClaim.getById(shiftTimeEntryId));
    },
    enabled: hasShiftTimeEntry,
  });

  const hoursMutation = useMutation({
    mutationKey: ["shift", id, "hours"],
    mutationFn: async (variables: {
      id: string;
      startTime: string;
      endTime: string;
      breakTime: number;
    }) => {
      if (!shift?.id) {
        throw new Error("Shift ID is required");
      }

      await api.shiftDeclaration.updateFullEntry(shift.id, {
        startAt: variables.startTime,
        endAt: variables.endTime,
        breakTime: variables.breakTime,
        shiftId: shift.id,
      });
    },
    onMutate: () => {
      toast.loading(t("actions:declare-hours.loading"), { id: TOAST_KEY });
    },
    onSuccess: () => {
      toast.success(t("actions:declare-hours.success"), { id: TOAST_KEY });
    },
    onError: (error) => {
      toast.error(t("actions:declare-hours.error"), { id: TOAST_KEY });
      logger.error(error);
    },
  });

  const qrClockInMutation = useMutation({
    mutationKey: ["shift", id, "qr-clock-in"],
    mutationFn: async (qrCodeData: string) => {
      if (!shift?.project?.id) {
        throw new Error("Project ID is required");
      }

      return await extractionWrapper(
        api.shiftDeclaration.clockIn({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      toast.loading(t("common:shift.clock-in.loading"), { id: TOAST_KEY });
    },
    onSuccess: () => {
      toast.success(t("common:shift.clock-in.success"), { id: TOAST_KEY });
      queryClient.invalidateQueries({ queryKey: ["shift", id] });
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error: any) => {
      const message = error?.message || t("common:shift.clock-in.error");
      toast.error(message, { id: TOAST_KEY });
      logger.error("QR clock-in error", error);
    },
  });

  const qrClockOutMutation = useMutation({
    mutationKey: ["shift", id, "qr-clock-out"],
    mutationFn: async (qrCodeData: string) => {
      if (!shift?.project?.id) {
        throw new Error("Project ID is required");
      }

      return await extractionWrapper(
        api.shiftDeclaration.clockOut({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      toast.loading(t("common:shift.clock-out.loading"), { id: TOAST_KEY });
    },
    onSuccess: () => {
      toast.success(t("common:shift.clock-out.success"), { id: TOAST_KEY });
      queryClient.invalidateQueries({ queryKey: ["shift", id] });
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error: any) => {
      const message = error?.message || t("common:shift.clock-out.error");
      toast.error(message, { id: TOAST_KEY });
      logger.error("QR clock-out error", error);
    },
  });

  const {
    scanQRCode: scanClockIn,
    isScanning: isScanningClockIn,
    WebScannerComponent: ClockInWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockInMutation.mutate(data),
  });

  const {
    scanQRCode: scanClockOut,
    isScanning: isScanningClockOut,
    WebScannerComponent: ClockOutWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockOutMutation.mutate(data),
  });

  const breakTimeMutation = useMutation({
    mutationKey: ["shift", id, "break-time"],
    mutationFn: async (breakTimeMinutes: number) => {
      if (!shift?.id) {
        throw new Error("Shift ID is required");
      }

      return await extractionWrapper(
        api.shiftDeclaration.clockBreakTime({
          shiftId: shift.id,
          breakTimeMinutes,
        }),
      );
    },
    onMutate: () => {
      toast.loading(t("shift:break-time.loading"), { id: TOAST_KEY });
    },
    onSuccess: () => {
      toast.success(t("shift:break-time.success"), { id: TOAST_KEY });
      queryClient.invalidateQueries({ queryKey: ["shift", id] });
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: (error: any) => {
      const message = error?.message || t("shift:break-time.error");
      toast.error(message, { id: TOAST_KEY });
      logger.error("Break time clock error", error);
    },
  });

  const [formHours, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      startTime: shift?.startAt ? format(shift.startAt, "HH:mm") : "",
      endTime: shift?.endAt ? format(shift.endAt, "HH:mm") : "",
      breakDuration: "00:00",
    },
    // FUTURE: Possibly validate the values here. Check if the break duration is correct
    onSubmit: async ({ value }) => {
      await hoursMutation.mutateAsync(
        {
          id: shift?.id ?? id!,
          ...value,
          breakTime: durationToMinutes(parseTimeString(value.breakDuration)),
        },
        {
          onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["shift"] });
            queryClient.invalidateQueries({ queryKey: ["shifts"] });
          },
        },
      );

      router.push(Routes.Shift({ id }, { search: { modal: undefined } }), undefined, "replace");
    },
  });

  if (!id) {
    return <Redirect to={Routes.CompletedShifts()} />;
  }

  // Only calculate duration if we have both start and end times
  const duration =
    hasStartTime && hasEndTime
      ? calculateShiftDuration(
          shift?.shiftTimeEntry?.startAt ?? shift?.startAt ?? "",
          shift?.shiftTimeEntry?.endAt ?? shift?.endAt ?? "",
          shift?.shiftTimeEntry?.breakTimeMinutes ?? 0,
        )
      : { hours: 0, minutes: 0 };

  const breakTime = shift?.shiftTimeEntry?.breakTimeMinutes;

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([refetchShift(), refetchExpenseClaims()]);
    event.detail.complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
          <Buttons slot="start">
            <BackButton defaultHref={Routes.CompletedShifts()} />
          </Buttons>
          <IonTitle>{shift?.project?.name}</IonTitle>
        </Toolbar>
      </IonHeader>
      <Content className="part-scroll:gap-6">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold">{t("routes:shift.worked-hours")}</h2>
          {shift && (
            <>
              {hasEndTime ? (
                <time dateTime={formatDurationToISO(duration)} className="flex gap-2 font-bold">
                  {!hasShiftTimeEntry && (
                    <span className="my-auto h-full text-2xl font-bold">~</span>
                  )}
                  <span className="flex items-baseline gap-1">
                    {Intl.NumberFormat(i18n.language, {
                      style: "unit",
                      unit: "hour",
                      unitDisplay: "short",
                    })
                      .formatToParts(duration.hours)
                      .filter((part) => part.type !== "literal" && part.value !== " ")
                      .map((part) => (
                        <span
                          key={JSON.stringify(part)}
                          className={cx(
                            part.type === "integer" && "text-2xl",
                            part.type === "unit" && "text-xl",
                          )}
                        >
                          {part.value}
                        </span>
                      ))}
                  </span>
                  <span className="flex items-baseline gap-1">
                    {Intl.NumberFormat(i18n.language, {
                      style: "unit",
                      unit: "minute",
                      unitDisplay: "short",
                    })
                      .formatToParts(duration.minutes)
                      .filter((part) => part.type !== "literal" && part.value !== " ")
                      .map((part) => (
                        <span
                          key={JSON.stringify(part)}
                          className={cx(
                            part.type === "integer" && "text-2xl",
                            part.type === "unit" && "text-xl",
                          )}
                        >
                          {part.value}
                        </span>
                      ))}
                  </span>
                </time>
              ) : (
                <div className="flex items-center gap-2 text-2xl font-bold">
                  <span>{t("routes:shift.in-progress")}</span>
                </div>
              )}
              <time dateTime={shift.startAt} className="font-medium text-txt-secondary">
                {Intl.DateTimeFormat(i18n.language, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }).format(new Date(shift.startAt))}
              </time>
            </>
          )}
        </div>
        {shift && (
          <div className="flex gap-3 *:flex-1">
            <IonCard className="flex flex-col px-3 py-2.5">
              <span className="text-xs font-medium text-txt-secondary">
                {t("routes:shift.start")}
              </span>
              <time dateTime={shift.startAt} className="mt-1 text-xl font-bold">
                <span className="flex items-baseline gap-1">
                  {!hasShiftTimeEntry && <span className="text-base">~</span>}
                  <span>
                    {Intl.DateTimeFormat(i18n.language, {
                      hour: "2-digit",
                      minute: "2-digit",
                    }).format(new Date(shift.shiftTimeEntry?.startAt ?? shift.startAt))}
                  </span>
                </span>
              </time>
            </IonCard>
            <IonCard className="flex flex-col px-3 py-2.5">
              <span className="text-xs font-medium text-txt-secondary">
                {t("routes:shift.break")}
              </span>
              <div className="mt-1 text-xl font-bold">
                {breakTime == null || breakTime === 0 ? (
                  <span>-</span>
                ) : (
                  <time dateTime={formatDurationToISO(minutesToDuration(breakTime))}>
                    {formatDurationToTimeString(minutesToDuration(breakTime))}
                  </time>
                )}
              </div>
            </IonCard>
            <IonCard className="flex flex-col px-3 py-2.5">
              <span className="text-xs font-medium text-txt-secondary">
                {t("routes:shift.end")}
              </span>
              <div className="mt-1 text-xl font-bold">
                {shift.shiftTimeEntry?.endAt ? (
                  <time dateTime={shift.shiftTimeEntry.endAt}>
                    <span className="flex items-baseline gap-1">
                      <span>
                        {Intl.DateTimeFormat(i18n.language, {
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(new Date(shift.shiftTimeEntry.endAt))}
                      </span>
                    </span>
                  </time>
                ) : shift.endAt ? (
                  <time dateTime={shift.endAt}>
                    <span className="flex items-baseline gap-1">
                      <span className="text-base">~</span>
                      <span>
                        {Intl.DateTimeFormat(i18n.language, {
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(new Date(shift.endAt))}
                      </span>
                    </span>
                  </time>
                ) : (
                  <span>-</span>
                )}
              </div>
            </IonCard>
          </div>
        )}
        <div className="flex flex-col gap-3">
          <h3 className="text-lg font-bold">{t("routes:shift.status")}</h3>
          <ShiftStatusCard
            shiftTimeEntry={shift?.shiftTimeEntry}
            allowEmployeeSubmitHours={allowEmployeeSubmitHours}
            status={shift?.shiftTimeEntry?.status}
          />
        </div>
        {!isProjectFinalized && (
          <>
            {enableQRCodeClocking ? (
              <>
                {/* QR Code Clock-in/out buttons - hide when shift is completed */}
                {!isClockedOut && (
                  <QRClockButtons
                    shiftStartAt={shift?.startAt || ""}
                    shiftEndAt={shift?.endAt || ""}
                    isClockedIn={isClockedIn}
                    isClockedOut={isClockedOut}
                    onClockIn={() => {
                      if (!qrClockInMutation.isPending && !isScanningClockIn) {
                        scanClockIn();
                      }
                    }}
                    onClockOut={() => {
                      if (!qrClockOutMutation.isPending && !isScanningClockOut) {
                        scanClockOut();
                      }
                    }}
                    isLoading={
                      qrClockInMutation.isPending ||
                      qrClockOutMutation.isPending ||
                      isScanningClockIn ||
                      isScanningClockOut
                    }
                  />
                )}
                {/* Break time button - shown when clocked out and break time is null/undefined */}
                {isClockedOut && breakTime === null && (
                  <>
                    <BreakTimeButton
                      onClick={() => setShowBreakTimeModal(true)}
                      isLoading={breakTimeMutation.isPending}
                    />
                  </>
                )}
              </>
            ) : (
              // Regular hours declaration button
              !hasShiftTimeEntry &&
              allowEmployeeSubmitHours && (
                <Button
                  onClick={() =>
                    router.push(
                      Routes.Shift({ id }, { search: { modal: "hours" } }),
                      undefined,
                      "replace",
                    )
                  }
                >
                  <div className="flex items-center gap-2">
                    {t("modals:declare-hours.title")}
                    <IonIcon icon={timeIcon} className="size-4" />
                  </div>
                </Button>
              )
            )}
          </>
        )}
        {!enableQRCodeClocking && (
          <ModalSheetAutoHeight
            isOpen={modal === "hours"}
            onWillDismiss={() =>
              router.push(
                Routes.Shift({ id }, { search: { modal: undefined } }),
                undefined,
                "replace",
              )
            }
            onDidDismiss={() => formHours.reset()}
          >
            <FormElement>
              <IonHeader>
                <IonToolbar>
                  <Buttons slot="start">
                    <Button
                      color="primary"
                      onClick={() =>
                        router.push(
                          Routes.Shift({ id }, { search: { modal: undefined } }),
                          undefined,
                          "replace",
                        )
                      }
                    >
                      <IonIcon slot="icon-only" icon={crossIcon} className="size-6" />
                    </Button>
                  </Buttons>
                  <IonTitle>{t("modals:declare-hours.title")}</IonTitle>
                </IonToolbar>
              </IonHeader>
              <div className="ion-padding flex flex-col gap-6">
                <formHours.AppField
                  name="startTime"
                  validators={{
                    onBlur: validator.time({
                      name: "startTime",
                      schema: validator.required({ name: "startTime" }),
                    }),
                    onSubmit: validator.time({
                      name: "startTime",
                      schema: validator.required({ name: "startTime" }),
                    }),
                  }}
                  children={(field) => (
                    <field.InputField
                      type="time"
                      className="time-input"
                      required
                      labelPlacement="start"
                      data-force-24h="true"
                      withAsterisk={false}
                    />
                  )}
                />
                <formHours.AppField
                  name="endTime"
                  validators={{
                    onBlur: validator.time({
                      name: "endTime",
                      schema: validator.required({ name: "endTime" }),
                    }),
                    onSubmit: validator.time({
                      name: "endTime",
                      schema: validator.required({ name: "endTime" }),
                    }),
                  }}
                  children={(field) => (
                    <field.InputField
                      type="time"
                      className="time-input"
                      required
                      labelPlacement="start"
                      data-force-24h="true"
                      withAsterisk={false}
                    />
                  )}
                />
                <formHours.AppField
                  name="breakDuration"
                  validators={{
                    onBlur: validator.time({
                      name: "breakDuration",
                      schema: validator.required({ name: "breakDuration" }),
                    }),
                    onSubmit: validator.time({
                      name: "breakDuration",
                      schema: validator.required({ name: "breakDuration" }),
                    }),
                  }}
                  children={(field) => (
                    <field.InputField
                      type="time"
                      className="time-input"
                      required
                      labelPlacement="start"
                      data-force-24h="true"
                      withAsterisk={false}
                    />
                  )}
                />
              </div>
              <IonFooter>
                <IonToolbar className="ion-pb-4 ion-pt-3">
                  <formHours.Subscribe
                    selector={(state) => [state.canSubmit, state.isSubmitting] as const}
                    children={([canSubmit, isSubmitting]) => (
                      <Button type="submit" disabled={!canSubmit} expand="block">
                        {isSubmitting ? t("common:loading") : t("actions:save.action")}
                      </Button>
                    )}
                  />
                </IonToolbar>
              </IonFooter>
            </FormElement>
          </ModalSheetAutoHeight>
        )}
        {allowTravelExpenses && (
          <TravelExpensesSection
            id={id!}
            travelExpensesDeclared={0}
            modal={modal}
            shiftTimeEntryId={shift?.shiftTimeEntry?.id}
            expenseClaim={expenseClaims}
          />
        )}
        {!!shift?.note && (
          <div className="flex flex-col gap-3">
            <h2 className="text-xl font-bold">{t("routes:shift.note")}</h2>

            <p className="font-medium text-txt-secondary">{shift.note}</p>
          </div>
        )}
      </Content>
      <BreakTimeModal
        isOpen={showBreakTimeModal}
        onDidDismiss={() => setShowBreakTimeModal(false)}
        onSubmit={async (breakTimeMinutes) => {
          await breakTimeMutation.mutateAsync(breakTimeMinutes);
          setShowBreakTimeModal(false);
        }}
        defaultBreakTime={breakTime ?? undefined}
      />
      {ClockInWebScanner && <ClockInWebScanner />}
      {ClockOutWebScanner && <ClockOutWebScanner />}
    </IonPage>
  );
}
